import type { AsyncJob } from "../types/api";

const NORTHFLANK_LOG_ACCESS_PATTERN = /log northflank non accessibile|impossibile leggere i log northflank/i;
const NORTHFLANK_AUTH_PATTERN = /\b(401|403)\b|unauthorized|forbidden/i;
const NORTHFLANK_RATE_LIMIT_PATTERN = /\brate[-\s]?limited\b|\b429\b|too many requests/i;

const isActiveNorthflankJob = (job: AsyncJob) =>
  job.source === "northflank" && (job.status === "queued" || job.status === "running");

const compactText = (value?: string | null) => String(value || "").trim().replace(/\s+/g, " ");

export const isNorthflankLogAccessWarning = (job: AsyncJob) => {
  if (!isActiveNorthflankJob(job)) return false;
  const latestMessage = compactText(job.latest_message);
  const syncError = compactText(job.sync_error);
  const combined = `${latestMessage} ${syncError}`.trim();
  return NORTHFLANK_LOG_ACCESS_PATTERN.test(combined) && NORTHFLANK_AUTH_PATTERN.test(combined);
};

export const isNorthflankRateLimitWarning = (job: AsyncJob) => {
  if (job.source !== "northflank") return false;
  if (job.remote_state === "rate_limited") return true;
  const combined = `${compactText(job.latest_message)} ${compactText(job.sync_error)} ${compactText(job.remote_message)}`.trim();
  return NORTHFLANK_RATE_LIMIT_PATTERN.test(combined);
};

export const getAsyncJobDisplayMessage = (job: AsyncJob) => {
  const latestMessage = compactText(job.latest_message);
  if (isNorthflankRateLimitWarning(job)) {
    const retryAt = compactText(job.remote_retry_at);
    return retryAt
      ? `Northflank rate-limited. Uso log condivisi/cached fino a ${retryAt}.`
      : "Northflank rate-limited. Uso log condivisi/cached.";
  }
  if (!isNorthflankLogAccessWarning(job)) {
    return latestMessage || "Nessun log disponibile";
  }

  const httpDetail = latestMessage.match(/\(HTTP [^)]+\)/i)?.[0]
    || compactText(job.sync_error).match(/\b(401|403)\b.*$/i)?.[0]
    || "autorizzazione mancante";

  return `Job Northflank avviato. Log remoti non accessibili dal frontend (${httpDetail}).`;
};

export const inferAsyncLogToneFromText = (text: string) => {
  const lower = text.toLowerCase();
  if (NORTHFLANK_RATE_LIMIT_PATTERN.test(lower)) return "warning" as const;
  if (NORTHFLANK_LOG_ACCESS_PATTERN.test(lower) && NORTHFLANK_AUTH_PATTERN.test(lower)) return "warning" as const;
  if (/\b(error|errore|failed|fallito)\b/.test(lower)) return "error" as const;
  if (/\b(warn|warning|attenzione|avviso)\b/.test(lower)) return "warning" as const;
  if (/\b(success|ok|completat[ao])\b/.test(lower) || text.includes("✅")) return "success" as const;
  if (/\b(heartbeat|streaming|run )\b/.test(lower)) return "muted" as const;
  return "info" as const;
};
